import { LogOut } from 'lucide-react';
import Button from '@/components/ui/Button';

interface LogoutUser {
  photoURL?: string;
  displayName?: string;
  email?: string;
}

export interface LogoutConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  user: LogoutUser | null;
}

const LogoutConfirmModal = ({ isOpen, onClose, onConfirm, user }: LogoutConfirmModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-60 w-full max-w-md mx-4 rounded-xl shadow-2xl transition-all bg-white border border-slate-200 dark:bg-slate-900 dark:border-slate-700">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-50 dark:bg-red-900/20">
              <LogOut size={20} strokeWidth={1.75} aria-hidden="true" className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">
                Confirm logout
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Are you sure you want to sign out?
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <div className="flex items-center space-x-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
            <img
              src={user?.photoURL || '/default-avatar.png'}
              alt={user?.displayName || 'User avatar'}
              className="w-8 h-8 rounded-full"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                const target = e.currentTarget;
                target.onerror = null;
                target.src = '/default-avatar.png';
              }}
            />
            <div>
              <p className="font-medium text-sm text-slate-900 dark:text-slate-200">
                {user?.displayName || 'Healthcare professional'}
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t gap-3 flex justify-end border-slate-200 dark:border-slate-700">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LogoutConfirmModal;
